import { ENVIRONMENTS, getEnv } from "@/types/env";
import { useEffect, useRef } from "react";
import {
  useFlags,
  useLDClient,
  type LDFlagSet,
} from "launchdarkly-react-client-sdk";
import { type User } from "@/types/localstorage";
import { coerceToString } from "@/utils/utils";

const env = getEnv(process.env.VITE_ENV);

const getLaunchDarklyClientId = (): string => {
  switch (env) {
    case ENVIRONMENTS.LOCAL:
      return "660e7b6035d04c0ff3ebec6b";
    case ENVIRONMENTS.DEV:
      return "660e7b6035d04c0ff3ebec6b";
    case ENVIRONMENTS.PROD:
      return "6604289eda208210134ba935";
    default:
      return "";
  }
};

const launchDarklyClientId = getLaunchDarklyClientId();

const launchDarklyPrivateAttributes = ["email"];

interface LaunchDarklyUserContext {
  kind: "user";
  key?: string;
  anonymous?: boolean;
  email?: string;
  _meta?: {
    privateAttributes: string[];
  };
}

const anonymousLaunchDarklyContext: LaunchDarklyUserContext = {
  kind: "user",
  anonymous: true,
};

const getLaunchDarklyUserContext = (
  user: Pick<User, "id" | "email"> | null | undefined
): LaunchDarklyUserContext => {
  if (!user?.id) {
    return anonymousLaunchDarklyContext;
  }

  return {
    kind: "user",
    key: user.id,
    email: user.email,
    _meta: {
      privateAttributes: launchDarklyPrivateAttributes,
    },
  };
};

const hasLoginCookie = (): boolean =>
  document.cookie.split("; ").some((cookie) => cookie === "isLogin=true");

const getStoredLaunchDarklyContext = (): LaunchDarklyUserContext => {
  if (!hasLoginCookie()) {
    return anonymousLaunchDarklyContext;
  }

  const storedData = localStorage.getItem("user");
  if (!storedData) {
    return anonymousLaunchDarklyContext;
  }

  try {
    const storedUserData = JSON.parse(storedData) as Record<string, unknown>;
    const user = {
      id: coerceToString(storedUserData.id),
      email:
        typeof storedUserData.email === "string" ? storedUserData.email : "",
    };

    return getLaunchDarklyUserContext(user);
  } catch {
    return anonymousLaunchDarklyContext;
  }
};

const useFeatureFlags = (): LDFlagSet => useFlags();

const useIdentifyLaunchDarklyUser = (
  isLoggedIn: boolean,
  user: Pick<User, "id" | "email"> | null | undefined
): void => {
  const ldClient = useLDClient();
  const lastIdentifiedContext = useRef<string>("");

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    const context = isLoggedIn
      ? getLaunchDarklyUserContext(user)
      : anonymousLaunchDarklyContext;
    const contextKey =
      isLoggedIn && user?.id ? `${user.id}:${user.email}` : "anonymous";

    if (lastIdentifiedContext.current === contextKey) {
      return;
    }

    lastIdentifiedContext.current = contextKey;
    void ldClient.identify(context);
  }, [isLoggedIn, ldClient, user?.email, user?.id]);
};

export {
  getStoredLaunchDarklyContext,
  launchDarklyClientId,
  launchDarklyPrivateAttributes,
  useFeatureFlags,
  useIdentifyLaunchDarklyUser,
};
